import { Form, Input, Table, Tag } from "antd";
import DatePicker from "../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../nestserver";
import { selectUser } from "../../../../redux/slices/auth.slice";

const { RangePicker } = DatePicker;

const AttendanceHome = () => {
  const user = useSelector(selectUser);
  const [form] = Form.useForm();
  const [studentId, setStudentId] = useState();
  const [studentResponse, setStudentResponse] = useState();
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: moment().subtract(14, "days"),
    to: moment(),
  });

  useEffect(() => {
    if (user && user?.student) {
      setStudentId(user?.student?._id);
    }
  }, [user]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange({ from: dates[0], to: dates[1] });
    }
  };

  const fetchStudentAttendance = async () => {
    const fromDate = dateRange.from.format("YYYY-MM-DD");
    const toDate = dateRange.to.format("YYYY-MM-DD");
    const res = await axios.get(
      `${serverUrl}/montessori/attendance/student/${studentId}?fromDate=${fromDate}&toDate=${toDate}`,
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      },
    );
    return res.data;
  };

  const { isLoading } = useQuery(
    ["attendance-student", studentId, dateRange],
    fetchStudentAttendance,
    {
      enabled: !!studentId,
      onSuccess: (response) => {
        const resultData = response?.result;
        const studentData =
          Array.isArray(resultData) && resultData.length > 0
            ? resultData[0]
            : resultData?.data?.[0] || {};

        const sortedAttendance = [...(studentData?.attendance || [])].sort(
          (a, b) => moment(b?.date).valueOf() - moment(a?.date).valueOf(),
        );

        setStudentResponse(studentData);
        setFilteredData(sortedAttendance);
      },
      onError: (err) => {
        console.error(err);
      },
    },
  );

  const columns = [
    {
      title: "Date",
      key: "date",
      align: "center",
      render: (record) => (
        <div style={{ color: "#111111" }} className="text-base text-center">
          {moment(record?.date).format("DD-MM-YYYY")}
        </div>
      ),
    },
    {
      title: "Morning",
      key: "morning",
      align: "center",
      render: (record) => (
        <div style={{ color: "#111111" }} className="text-base text-center">
          {record?.holiday?.isHoliday ? (
            <span className="text-blue-500 font-medium">Holiday</span>
          ) : record?.permission?.hasPermission ? (
            <span className="text-red-500 font-medium">❌ On Leave</span>
          ) : record?.is_checked_in ? (
            <span className="text-green-600 font-medium">Present</span>
          ) : record?.not_marked ? (
            <span className="text-gray-400 font-medium">Not Marked</span>
          ) : (
            <span className="text-red-500 font-medium">Absent</span>
          )}
        </div>
      ),
    },
    {
      title: "Afternoon",
      key: "afternoon",
      align: "center",
      render: (record) => (
        <div style={{ color: "#111111" }} className="text-base text-center">
          {record?.holiday?.isHoliday ? (
            <span className="text-blue-500 font-medium">Holiday</span>
          ) : record?.permission?.hasPermission ? (
            <span className="text-red-500 font-medium">❌ On Leave</span>
          ) : record?.is_checked_out ? (
            <span className="text-green-600 font-medium">Present</span>
          ) : record?.not_marked ? (
            <span className="text-gray-400 font-medium">Not Marked</span>
          ) : (
            <span className="text-red-500 font-medium">Absent</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (record) => {
        if (record?.permission?.hasPermission) {
          return <Tag color="orange">{record.permission.permissionType}</Tag>;
        }
        if (record?.holiday?.isHoliday) {
          return <Tag color="blue">Holiday</Tag>;
        }
        const morningExcused = !!record?.permission?.morningPermission && !record?.is_checked_in;
        const afternoonExcused = !!record?.permission?.afternoonPermission && !record?.is_checked_out;
        if (morningExcused || afternoonExcused) {
          const otherOk =
            (morningExcused || record?.is_checked_in) &&
            (afternoonExcused || record?.is_checked_out);
          return (
            <Tag
              color={
                morningExcused && afternoonExcused
                  ? "blue"
                  : otherOk
                    ? "orange"
                    : "red"
              }
            >
              {morningExcused && afternoonExcused
                ? "Permission"
                : otherOk
                  ? "Half Day (Permission)"
                  : "Absent"}
            </Tag>
          );
        }

        if (record?.is_checked_in && record?.is_checked_out) {
          return <Tag color="green">Present</Tag>;
        } else if (
          (record?.is_checked_in && !record?.is_checked_out) ||
          (!record?.is_checked_in && record?.is_checked_out)
        ) {
          return <Tag color="orange">Half Day</Tag>;
        } else if (record?.is_checked_in || record?.is_checked_out) {
          return <Tag color="green">Present</Tag>;
        } else if (record?.not_marked) {
          return <Tag color="default">Not Marked</Tag>;
        } else {
          return <Tag color="red">Absent</Tag>;
        }
      },
    },
  ];

  return (
    <div className="">
      <div className="pt-8">
        <div className="bg-white p-10 rounded-2xl">
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            My Attendance
          </div>
          <Form
            layout="vertical"
            name="basic"
            form={form}
            initialValues={{ date: [dateRange.from, dateRange.to] }}
            autoComplete="off"
            className="w-1/2"
          >
            <Form.Item label="Attendance %" className="w-full">
              <Input
                readOnly
                value={`${Math.round(studentResponse?.attendancePercent || 0)}%`}
              />
            </Form.Item>
            <Form.Item label="Choose a range" name="date" className="w-full">
              <RangePicker
                format={"DD/MM/YYYY"}
                value={[dateRange.from, dateRange.to]}
                disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
                className="w-full"
                onChange={handleDateRangeChange}
              />
            </Form.Item>
          </Form>
          <div className="pt-5 attendance-table">
            <Table
              columns={columns}
              className="w-full"
              dataSource={
                filteredData && filteredData.length > 0 ? filteredData : []
              }
              loading={isLoading}
              rowKey={(record) => record.date}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceHome;
